"use client";

import React, { useState, useEffect } from "react";
import { ArrowRight, Activity, TrendingUp, Users, ChartBar, Play } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  month: "Jan",
  subscribers: 1200
}, {
  month: "Feb",
  subscribers: 1350
}, {
  month: "Mar",
  subscribers: 1600
}, {
  month: "Apr",
  subscribers: 2100
}, {
  month: "Maj",
  subscribers: 2800
}, {
  month: "Jun",
  subscribers: 3500
}, {
  month: "Jul",
  subscribers: 4800
}];
const chartConfig = {
  subscribers: {
    label: "Användare",
    color: "hsl(var(--primary))",
    icon: Users
  }
};
export default function MasterYourChannelStatistics() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <section data-section-id="3780" id="framtiden-är-autonom" ref={sectionRef} className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-32 min-h-[90vh] flex items-center">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {}
        <div className="absolute inset-0 opacity-40 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1696446701796-da61225697cc?q=80&w=2070&auto=format&fit=crop')"
      }} />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/40 to-transparent" />
        
        {}
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {}
          <div className={`lg:col-span-7 flex flex-col gap-8 transition-all duration-1000 transform ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-[3px] bg-primary/10 border border-primary/20 w-fit">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              <span className="text-xs font-medium text-primary tracking-widest uppercase font-sans">Autonom Mobilitet</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] font-serif text-white">
              Framtiden är autonom med{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/80 to-white">
                RobotaxiNU
              </span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed font-sans">
              Sverige står inför en revolution inom transport. Med AI-drivna fordon från Tesla, Waymo och XPeng förändras allt – från lagstiftning till hur vi äger och använder bilar i vår vardag.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <Button size="lg" className="relative overflow-hidden group bg-primary hover:bg-primary/90 text-white px-8 h-14 text-lg shadow-lg rounded-[3px] transition-all duration-300" asChild>
                <Link to="/BecomeOperatorPage">
                  <span className="relative z-10 flex items-center gap-2">
                    <Play className="w-5 h-5 fill-current" />
                    Bli Operatör
                  </span>
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </Link>
              </Button>
              
              <Button variant="outline" size="lg" className="h-14 text-lg border-white/20 text-white hover:bg-white/5 rounded-[3px]" asChild>
                <Link to="/InsightsPage">
                  Läs om tekniken
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-8 border-t border-white/10 pt-8">
              {[{
              label: "Förväntad Start",
              value: "2025/26"
            }, {
              label: "Säkerhet",
              value: "99.9%"
            }, {
              label: "Besparing",
              value: "70%"
            }].map((stat, i) => <div key={i} data-index={i}>
                  <div className="text-2xl md:text-3xl font-bold text-white font-serif">{stat.value}</div>
                  <div className="text-sm text-white/60 font-sans">{stat.label}</div>
                </div>)}
            </div>
          </div>

          {}
          <div className={`lg:col-span-5 relative perspective-[2000px] transition-all duration-1000 delay-300 transform ${isVisible ? "translate-x-0 opacity-100" : "translate-x-20 opacity-0"}`}>
            {}
            <div className="relative transform transition-transform duration-500 hover:scale-[1.02] hover:rotate-y-[-5deg] hover:rotate-x-[5deg]" style={{
            transformStyle: "preserve-3d",
            transform: "rotateY(-12deg) rotateX(5deg)"
          }}>
              {}
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-primary/5 blur-2xl -z-10 transform translate-z-[-50px]" />

              <Card className="bg-card/80 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden rounded-[3px]">
                <CardHeader className="border-b border-white/5 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl flex items-center gap-2 font-serif text-primary">
                        <Activity className="w-5 h-5" />
                        Flottans Tillväxt
                      </CardTitle>
                      <CardDescription className="font-sans">Prognos för passagerare</CardDescription>
                    </div>
                    <div className="flex items-center gap-2 text-green-400 bg-green-400/10 px-2 py-1 rounded-[3px] text-sm font-medium">
                      <TrendingUp className="w-4 h-4" />
                      +24.5%
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="h-[250px] w-full">
                    {mounted && <ChartContainer config={chartConfig} className="h-full w-full">
                        <AreaChart data={chartData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }}>
                          <defs>
                            <linearGradient id="fillSubscribers" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                          <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={value => value.slice(0, 3)} stroke="rgba(255,255,255,0.5)" fontSize={12} />
                          <YAxis tickLine={false} axisLine={false} tickFormatter={value => `${value}`} stroke="rgba(255,255,255,0.5)" fontSize={12} width={40} />
                          <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" hideLabel />} />
                          <Area dataKey="subscribers" type="natural" fill="url(#fillSubscribers)" fillOpacity={0.4} stroke="var(--color-primary)" strokeWidth={3} stackId="a" />
                        </AreaChart>
                      </ChartContainer>}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="bg-white/5 rounded-[3px] p-3 border border-white/5">
                      <div className="text-xs text-white/50 mb-1 font-sans">Resor/dag</div>
                      <div className="text-lg font-bold flex items-center gap-2 text-white font-serif">
                        1.2M 
                        <ChartBar className="w-4 h-4 text-primary" />
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-[3px] p-3 border border-white/5">
                      <div className="text-xs text-white/50 mb-1 font-sans">Drifttid</div>
                      <div className="text-lg font-bold flex items-center gap-2 text-white font-serif">
                        24/7
                        <Activity className="w-4 h-4 text-primary" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {}
              <div className="absolute -top-6 -right-6 bg-card border border-white/10 p-4 rounded-[3px] shadow-xl animate-bounce duration-[3000ms]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-xs text-white/60 font-sans">Mål Uppnått</div>
                    <div className="font-bold text-sm text-white font-serif">Nivå 5 Autonomi</div>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}